import ReactDOM from 'react-dom'
import take from 'lodash/take'
import className from 'classnames'
import { useLocation } from 'react-router'
import { Fragment, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'

import { Link, matchI18nPathList } from 'features/i18n/router'
import { st } from 'lib/helpers'
import { User } from 'lib/_types'
import { ROUTE_PATH } from 'lib/paths'
import { MODAL_MODE } from './config'
import { selectLoginWithEmail } from 'store/ducks/auth'
import { useGetChatRoomQuery } from 'store/api/yay/chat'
import {
  useCurrentUser,
  useGetUserPolicyAgreementQuery,
} from 'store/api/yay/user'

import ChatListModal from './ChatListModal'
import NewChatModal from './NewChatModal'
import ChatRoomModal from './ChatRoomModal'
import ChatRoomDetailModal from './ChatRoomDetailModal'
import ReportModal from './ReportModal'
import UserReviewModal from './UserReviewModal'
import GroupInvitationModal from './GroupInvitationModal'
import CreateGroupModal from './CreateGroupModal'
import FollowRequestModal from './FollowRequestModal'
import RequestJoinGroupModal from './RequestJoinGroupModal'
import AllGroupsSettingsModal from './AllGroupsSettingsModal'
import GroupMembersModal from './GroupMembersModal'
import GroupSettingsModal from './GroupSettingsModal'
import GroupModeratorsModal from './GroupModeratorsModal'
import GroupTransferOwnershipModal from './GroupTransferOwnershipModal'
import GroupInCircleRankingModal from './GroupInCircleRankingModal'
import HimanowModal from './HimanowModal'
import PostBoxModal from './PostBoxModal'
import FollowerListModal from './FollowerListModal'
import UserReviewListModal from './UserReviewListModal'
import RequestChatListModal from './RequestChatListModal'
import SeoHeader from '../SEOHeader'
import UserEditModal from './UserEditModal'
import CallListModal from './CallListModal'
import SelectCategoryModal from './SelectCategoryModal'
import SelectCallSettingsModal from './SelectCallSettingsModal'
import TimelineFilterModal from './TimelineFilterModal'
import RelatedGroupsModal from './RelatedGroupsModal'
import AddRelatedGroupsModal from './AddRelatedGroupsModal'
import ActivityUsersBirthdayModal from './ActivityUsersBirthdayModal'
import MuteKeywordsModal from './MuteKeywordsModal'
import KeywordDetailsModal from './KeywordDetailsModal'
import GroupBanListModal from './GroupBanListModal'
import GroupMuteListModal from './GroupMuteListModal'
import LoginModal from './LoginModal'
import SignUpModal from './SignUpModal'
import SignUpFormModal from './SignUpFormModal'
import ThreadBoxModal from './ThreadBoxModal'
import ExistingThreadsModal from './ExistingThreadsModal'
import { InterestsSelectionModal } from './InterestsSelectionModal'
import { UserAgreementModal } from './UserAgreementModal'
import { ActivitySettingModal } from './ActivitySettingModal'
import { ActionSheetModal } from './ActionSheetModal/ActionSheetModal'
import SendGiftModal from './SendGiftModal'
import TransactionDetailsModal from './TransactionDetailsModal'
import GiftExchangeTransactionModal from './GiftExchangeTransactionModal'
import PurchaseEMPLModal from './PurchaseEMPLModal'
import OTPModal from './OTPModal'

export const ModalComp = {
  [MODAL_MODE.CHAT_LIST]: ChatListModal,
  [MODAL_MODE.REQUEST_CHAT_LIST]: RequestChatListModal,
  [MODAL_MODE.NEW_CHAT]: NewChatModal,
  [MODAL_MODE.CHAT_ROOM]: ChatRoomModal,
  [MODAL_MODE.CHAT_ROOM_DETAIL]: ChatRoomDetailModal,
  [MODAL_MODE.CREATE_USER_REVIEW]: UserReviewModal,
  [MODAL_MODE.GROUP_INVITATION]: GroupInvitationModal,
  [MODAL_MODE.GROUP_BAN_LIST]: GroupBanListModal,
  [MODAL_MODE.GROUP_MUTE_LIST]: GroupMuteListModal,
  [MODAL_MODE.CREATE_GROUP]: CreateGroupModal,
  [MODAL_MODE.FOLLOW_REQUEST]: FollowRequestModal,
  [MODAL_MODE.GROUP_JOIN_REQUEST]: RequestJoinGroupModal,
  [MODAL_MODE.GROUPS_SETTINGS]: AllGroupsSettingsModal,
  [MODAL_MODE.GROUP_MEMBER]: GroupMembersModal,
  [MODAL_MODE.GROUP_SETTINGS]: GroupSettingsModal,
  [MODAL_MODE.GROUP_MODERATORS]: GroupModeratorsModal,
  [MODAL_MODE.GROUP_TRANSFER_OWNERSHIP]: GroupTransferOwnershipModal,
  [MODAL_MODE.GROUP_IN_CIRCLE_RANKING]: GroupInCircleRankingModal,
  [MODAL_MODE.HIMANOW_USER_LIST]: HimanowModal,
  [MODAL_MODE.POSTBOX_MODAL]: PostBoxModal,
  [MODAL_MODE.SEND_GIFT_MODAL]: SendGiftModal,
  [MODAL_MODE.PURCHASE_EMPL]: PurchaseEMPLModal,
  [MODAL_MODE.USER_EDIT]: UserEditModal,
  [MODAL_MODE.USER_FOLLOWER]: FollowerListModal,
  [MODAL_MODE.USER_REVIEW_LIST]: UserReviewListModal,
  [MODAL_MODE.ACTIVITY_USERS_BIRTHDAY]: ActivityUsersBirthdayModal,
  [MODAL_MODE.CALL_LIST_MODAL]: CallListModal,
  [MODAL_MODE.SELECT_CATEGORY]: SelectCategoryModal,
  [MODAL_MODE.SELECT_CALL_SETTINGS]: SelectCallSettingsModal,
  [MODAL_MODE.TIMELINE_FILTER]: TimelineFilterModal,
  [MODAL_MODE.RELATED_GROUPS]: RelatedGroupsModal,
  [MODAL_MODE.ADD_RELATED_GROUPS]: AddRelatedGroupsModal,
  [MODAL_MODE.MUTE_KEYWORDS]: MuteKeywordsModal,
  [MODAL_MODE.KEYWORD_DETAILS]: KeywordDetailsModal,
  [MODAL_MODE.LOGIN]: LoginModal,
  [MODAL_MODE.SIGN_UP]: SignUpModal,
  [MODAL_MODE.SIGN_UP_FORM]: SignUpFormModal,
  [MODAL_MODE.INTERESTS_SELECTION]: InterestsSelectionModal,
  [MODAL_MODE.EXISTING_THREADS]: ExistingThreadsModal,
  [MODAL_MODE.USER_AGREEMENT]: UserAgreementModal,
  [MODAL_MODE.ACTIVITY_SETTINGS]: ActivitySettingModal,
  [MODAL_MODE.TRANSACTION_DETAILS]: TransactionDetailsModal,
  [MODAL_MODE.GIFT_EXCHANGE_TRANSACTION_DETAILS]: GiftExchangeTransactionModal,
}

const PageModal = () => {
  const location = useLocation()

  const { data: currentUser, isSuccess } = useCurrentUser()

  const loginWithEmailState = useSelector(selectLoginWithEmail)

  const [searchParams, setSearchParams] = useSearchParams()
  const searchValue: Record<string, string> = Object.fromEntries(searchParams)
  const { modalMode, chatRoomId } = searchValue

  const ModalElm = ModalComp?.[modalMode as string]
  const isActive = typeof modalMode !== 'undefined'

  const chatRoom = useGetChatRoomQuery(
    { chatId: Number(chatRoomId) },
    { skip: !chatRoomId }
  )

  const { data: agreement } = useGetUserPolicyAgreementQuery(
    {},
    { skip: !currentUser }
  )

  useEffect(() => {
    if (!isSuccess || !agreement) return

    // check user agreement to terms of use and privacy policy
    if (
      agreement.latest_privacy_policy_agreed &&
      agreement.latest_terms_of_use_agreed &&
      agreement.latest_empl_2_agreed
    ) {
      if (modalMode === MODAL_MODE.USER_AGREEMENT) {
        // already agreed all, but url want to show user agreement modal
        setSearchParams((prev) => {
          prev.delete('modalMode')
          prev.delete('privacy')
          prev.delete('terms')
          prev.delete('empl')
          prev.delete('returnPath')
          return prev
        })

        return
      }
    } else if (
      agreement.latest_privacy_policy_agreed !== undefined ||
      agreement.latest_terms_of_use_agreed !== undefined ||
      agreement.latest_empl_2_agreed !== undefined
    ) {
      // need user agreement
      setSearchParams((prev) => {
        if (modalMode !== MODAL_MODE.USER_AGREEMENT) {
          // backup search params
          prev.set('returnPath', location.pathname + location.search)
          prev.set('modalMode', MODAL_MODE.USER_AGREEMENT)
        }

        prev.delete('privacy')
        if (!agreement.latest_privacy_policy_agreed) prev.set('privacy', 'true')

        prev.delete('terms')
        if (!agreement.latest_terms_of_use_agreed) prev.set('terms', 'true')

        prev.delete('empl')
        if (!agreement.latest_empl_2_agreed) prev.set('empl', 'true')

        return prev
      })

      return
    }

    // check user favs topic in timeline page
    const isTimelinePage = matchI18nPathList([
      ROUTE_PATH.TIMELINE_FOLLOWING,
      ROUTE_PATH.TIMELINE_PUBLIC,
    ])

    if (
      isTimelinePage &&
      !modalMode &&
      currentUser?.interests_selected === false
    ) {
      setSearchParams((prev) => {
        prev.set('modalMode', MODAL_MODE.INTERESTS_SELECTION)
        if (location.search) {
          prev.set('returnPath', location.pathname + location.search)
        }
        return prev
      })
    }
  }, [
    modalMode,
    isSuccess,
    location.pathname,
    location.search,
    agreement,
    setSearchParams,
    currentUser,
  ])

  return ReactDOM.createPortal(
    <Fragment>
      {modalMode === MODAL_MODE.CHAT_LIST && (
        <SeoHeader
          description={st('chatrooms.description')}
          title={st('chatrooms.title')}
        />
      )}

      {(modalMode === MODAL_MODE.CHAT_ROOM ||
        modalMode === MODAL_MODE.CHAT_ROOM_DETAIL) && (
        <Fragment>
          {(() => {
            let name = ''

            if (chatRoom?.data?.is_group) {
              if (chatRoom?.data?.name) {
                name = chatRoom?.data?.name
              } else {
                const users = take(chatRoom?.data?.members ?? [], 3)

                name = users?.map?.((user: any) => user?.nickname)?.join?.(', ')
              }
            } else {
              const user = (chatRoom?.data?.members ?? [])?.find?.(
                (user: User) => user?.id !== currentUser?.id
              )

              name = user?.nickname
            }

            return (
              <SeoHeader
                description={st('chatroom.description', { name })}
                title={st('chatroom.title', { name })}
              />
            )
          })()}
        </Fragment>
      )}

      {/* Modal list that activated by `modalMode` search query. */}
      {ModalElm && (
        <>
          {(() => {
            // This is a gambiarra to fix https://jira.nanameue.jp/browse/YAY-13328.
            // The root cause is much more subtle and is about how we load current user and render layouts.
            // See more detail on https://github.com/nanameue/yay_web/pull/954.
            // Addendum: Hey guess what the same thing happens with the sign-up modal too sometimes :)
            if (
              (modalMode == MODAL_MODE.LOGIN && loginWithEmailState.isLoaded) ||
              (modalMode == MODAL_MODE.SIGN_UP_FORM && isSuccess)
            ) {
              return null
            }
            if (
              modalMode == MODAL_MODE.USER_AGREEMENT &&
              !searchValue.terms &&
              !searchValue.privacy &&
              !searchValue.empl
            ) {
              // if intended modal is user agreement but none of the agreement params have been set, don't render
              return null
            }

            return (
              <div
                className={className('Modal', {
                  'Modal--active': isActive,
                })}
              >
                {modalMode != MODAL_MODE.LOGIN && (
                  <Link
                    className="Modal__overlay"
                    to={location.pathname}
                    replace
                  />
                )}

                <ModalElm location={location} {...searchValue} />
              </div>
            )
          })()}
        </>
      )}

      <PostBoxModal />

      <OTPModal />

      <PurchaseEMPLModal />

      <SendGiftModal />

      <TransactionDetailsModal />

      <GiftExchangeTransactionModal />

      <ThreadBoxModal />

      <ReportModal />

      <ActionSheetModal />
    </Fragment>,
    document.querySelector('#modals')
  )
}

export default PageModal
